import { useEffect, useMemo, useRef, useState } from "react"
import { equals, ModelState, validateField } from "@tm/utils"
import { useLocalization } from "@tm/localization"
import { TextField, Text } from "@tm/controls"
import { Button, Box, Stack, Switch, Typography, Modal, LoadingContainer, Alert } from "@tm/components"
import { SubUser, Permission } from "../../../../data/model"
import { updateAuth0SubUser, createAuth0SubUser } from "../../../../data"
import { getBundleParams } from "../../../../utils"
import SubUserPermissions from "../SubUserPermissions"
import { SubUserEditorProps } from "."

export default function Auth0SubUserEditorComponent(props: SubUserEditorProps) {
    const { translateText, translate } = useLocalization()
    const { permissionContainer, type, headline, subUser, onclose } = props
    const [saving, setSaving] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>()
    const [serviceModelState, setServiceModelState] = useState<ModelState>()
    const [forceError, setForceError] = useState(false)
    const usernameInputRef = useRef<TextField | null>(null)
    const mailInputRef = useRef<TextField | null>(null)

    const firstnameLabel = type === "create" ? `${translateText(119)} *` : translateText(119)
    const lastnameLabel = type === "create" ? `${translateText(155)} *` : translateText(155)
    const mailLabel = type === "create" ? `${translateText(109)} *` : translateText(109)

    const permissionsAsFlatList = useMemo(() => {
        let permissions: Permission[] = []
        if (permissionContainer) {
            permissions = permissions.concat(
                permissionContainer.appPermissions,
                permissionContainer.catalogPermissions,
                permissionContainer.appAndCatalogPermissions
            )
        }
        return permissions
    }, [permissionContainer])

    const emptySubUser: SubUser = useMemo(() => {
        const { subUserDefaultRevokedRights } = getBundleParams()

        return { username: "", revokedRights: subUserDefaultRevokedRights }
    }, [])

    const [user, setUser] = useState<SubUser>({ ...(subUser || emptySubUser) })

    useEffect(() => {
        if (subUser) {
            setUser(subUser)
        }
    }, [subUser])

    const allPermissionsGranted = useMemo(() => {
        if (permissionContainer && user.revokedRights) {
            return !(
                permissionContainer.appPermissions.some((permission) => user.revokedRights?.includes(permission.key)) ||
                permissionContainer.catalogPermissions.some((permission) => user.revokedRights?.includes(permission.key)) ||
                permissionContainer.appAndCatalogPermissions.some((permission) => user.revokedRights?.includes(permission.key))
            )
        }
        // permissionContainer or user.revokedRights is undefined
        return true
    }, [permissionContainer, user.revokedRights])

    function handleClose() {
        onclose(false)
    }

    function handleSave() {
        const modifiedUser = { ...user }
        modifiedUser.passwordConfirm = undefined
        setSaving(true)
        setForceError(false)

        let promise
        switch (type) {
            case "create":
                promise = createAuth0SubUser(modifiedUser)
                break
            case "edit":
                promise = updateAuth0SubUser(modifiedUser)
                break
            default:
                break
        }
        if (promise) {
            promise.then(
                (result) => {
                    if (!!result && result.externalSucceed && result.mdmSucceed) {
                        onclose(true)
                    }
                    if (!!result && (!result.externalSucceed || result.mdmSucceed)) {
                        setSaving(false)
                        if (result.messages.length > 0) {
                            setErrorMessage(result.messages[0])
                        }
                    }
                },
                (error) => {
                    if (error) {
                        setSaving(false)
                        setForceError(true)
                        setServiceModelState(error.modelState)
                        if (error.modelState.eMail !== undefined) {
                            mailInputRef?.current?.focus()
                        } else {
                            usernameInputRef?.current?.focus()
                        }
                    }
                }
            )
        }
    }

    function handleReset() {
        setUser({ ...(subUser || emptySubUser) })
        setServiceModelState(undefined)
    }

    function handleChange(model: SubUser) {
        if (model.username !== user.username) {
            setUser({ ...model, eMail: model.username })
        } else {
            setUser(model)
        }
        setServiceModelState(undefined)
    }

    function handleAllOnOffChange(state: boolean) {
        if (state) {
            setUser({
                ...user,
                revokedRights: [],
            })
        } else if (permissionContainer) {
            const permissionKeys = permissionsAsFlatList.map((permission) => permission.key)

            setUser({
                ...user,
                revokedRights: permissionKeys,
            })
        }
    }

    function validate(userToValidate: SubUser): ModelState {
        const modelstate: ModelState = {
            username: validateField(userToValidate.username).email(translateText(290)).min(1, translateText(289)).messages,
            firstname: validateField(userToValidate.firstname).min(1, translateText(289)).messages,
            lastname: validateField(userToValidate.lastname).min(1, translateText(289)).messages,
        }

        return modelstate
    }

    const dirty = !equals(user, subUser || emptySubUser)

    const modelState = serviceModelState || validate(user)

    let valid = true
    Object.keys(modelState).forEach((key) => {
        const value = modelState[key]
        if (value && value.length) {
            valid = false
        }
    })

    return (
        <Modal open onOutsideClick={handleClose} smallCloseButton>
            <LoadingContainer isLoading={saving}>
                <Box className="user-settings__sub-user-editor" paddingLeft="1em">
                    <Typography marginLeft={1} variant="h1">
                        {translateText(headline)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} mb={1}>
                        <Button onClick={handleSave} disabled={!dirty || saving || !valid} color="success">
                            {translateText(9)}
                        </Button>
                        <Button onClick={handleReset} disabled={!dirty || saving}>
                            {translateText(48)}
                        </Button>
                    </Stack>
                    <Box className="user-settings__sub-user-editor__form">
                        {type === "create" && !errorMessage && (
                            <Text className="user-settings__sub-user-editor__form__hint" modifiers="block">
                                {translate(1751)}
                            </Text>
                        )}
                        {type === "edit" && !errorMessage && (
                            <>
                                <Typography marginLeft={1} marginTop={2}>
                                    {translateText(14071)}{" "}
                                </Typography>
                                <Typography marginLeft={1} marginTop={2}>
                                    {translateText(14072)}{" "}
                                </Typography>
                            </>
                        )}
                        {errorMessage && (
                            <Alert variant="outlined" size="small" severity="error">
                                {errorMessage}
                            </Alert>
                        )}

                        <Typography marginLeft={1} marginTop={2} variant="h3">
                            {translateText(14070)}
                        </Typography>
                        <TextField
                            ref={mailInputRef}
                            model={user}
                            path={["username"]}
                            onChange={handleChange}
                            modelState={modelState}
                            forceShowError={forceError}
                            label={mailLabel}
                            readonly={type === "edit"}
                        />

                        <Typography marginLeft={1} marginTop={2} variant="h3">
                            {translateText(1126)}
                        </Typography>
                        <TextField ref={usernameInputRef} model={user} path={["username"]} label={translateText(186)} autoComplete="off" disabled />
                        <Typography marginLeft={1} variant="label">
                            {translateText(13888)}
                        </Typography>
                        <TextField
                            model={user}
                            path={["firstname"]}
                            onChange={handleChange}
                            modelState={modelState}
                            label={firstnameLabel}
                            formatter={(value) => value.replace(/;/g, "")}
                            forceShowError={forceError}
                            readonly={type === "edit"}
                        />
                        <TextField
                            model={user}
                            path={["lastname"]}
                            onChange={handleChange}
                            modelState={modelState}
                            label={lastnameLabel}
                            formatter={(value) => value.replace(/;/g, "")}
                            forceShowError={forceError}
                            readonly={type === "edit"}
                        />
                        {!!permissionsAsFlatList?.length && (
                            <Box className="user-settings__sub-user-editor__permissions">
                                <Typography marginLeft={1} variant="h3">
                                    {translateText(1246)}
                                </Typography>
                                <Box sx={{ margin: 1, marginLeft: 1 }}>
                                    <Switch
                                        sx={{ marginLeft: 0.5 }}
                                        label={translateText(13511)}
                                        onChange={(state) => handleAllOnOffChange(state.target.checked)}
                                        checked={allPermissionsGranted}
                                    />
                                </Box>
                                <SubUserPermissions permissionContainer={permissionContainer} user={user} setUser={setUser} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </LoadingContainer>
        </Modal>
    )
}
